import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiUrl, getCsrfToken } from "../../utils/apiHelper";
import StatusResultModal from "../StatusResultModal";

function RedTeamingDocsTab() {
  const { t } = useTranslation("generate");
  const [maliciousDocsFileType, setMaliciousDocsFileType] = useState("1");
  const [maliciousDocsInputType, setMaliciousDocsInputType] = useState("prompts");
  const [maliciousDocsPrompt, setMaliciousDocsPrompt] = useState("");
  const [maliciousDocsFileName, setMaliciousDocsFileName] = useState("");
  const [maliciousDocsLoading, setMaliciousDocsLoading] = useState(false);
  const [maliciousDocsError, setMaliciousDocsError] = useState(null);
  const [maliciousDocsModal, setMaliciousDocsModal] = useState({ open: false, type: "", message: "" });

  const handleGenerateMaliciousDocs = async () => {
    if (!maliciousDocsPrompt.trim()) {
      setMaliciousDocsError(t("errors.enterPrompt"));
      return;
    }

    if (!maliciousDocsFileName.trim()) {
      setMaliciousDocsError(t("errors.enterFileName"));
      return;
    }

    const safeDocsFileName = maliciousDocsFileName.trim();

    setMaliciousDocsModal({ open: false, type: "", message: "" });
    setMaliciousDocsLoading(true);
    setMaliciousDocsError(null);

    try {
      const csrfToken = await getCsrfToken();

      const formData = new FormData();
      formData.append("opFileType", maliciousDocsFileType);
      formData.append("prompt", maliciousDocsPrompt);
      formData.append("opFileName", safeDocsFileName);

      const response = await fetch(getApiUrl("/api/internal/test-files/generate/malicious-docs/"), {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        body: formData,
      });

      let data;
      try {
        data = await response.json();
      } catch {
        throw new Error(t("errors.unexpectedMaliciousDocs"));
      }

      const apiSuccess = data && typeof data.success === "boolean" ? data.success : response.ok;
      const apiMessage = apiSuccess
        ? (data && data.message) || t("success.maliciousDocs")
        : (data && data.detail) || t("errors.httpStatus", { status: response.status });

      if (!apiSuccess) {
        throw new Error(apiMessage);
      }

      setMaliciousDocsModal({
        open: true,
        type: "success",
        message: apiMessage,
      });
    } catch (err) {
      console.error("Error generating malicious docs:", err);
      const message = err instanceof Error && err.message
        ? err.message
        : t("errors.unexpectedMaliciousDocsGeneric");
      setMaliciousDocsModal({
        open: true,
        type: "error",
        message,
      });
    } finally {
      setMaliciousDocsLoading(false);
    }
  };

  return (
    <>
      <div className="generate-form-container">
        <div className="generate-form-grid">
          <div className="generate-form-section generate-form-group-full">
            <div className="generate-section-header">
              <h4 className="generate-section-title">{t("redTeamingDocs.sourceContent")}</h4>
              <p className="generate-section-description">
                {t("redTeamingDocs.sourceContentDesc")}
              </p>
            </div>

            <div className="generate-form-group">
              <label className="generate-form-label">{t("redTeamingDocs.inputType")}</label>
              <p className="generate-field-description">
                {t("redTeamingDocs.inputTypeDesc")}
              </p>
              <div className="generate-radio-group">
                <label className={`generate-radio-option ${maliciousDocsInputType === "prompts" ? "active" : ""}`}>
                  <input
                    type="radio"
                    name="maliciousDocsInputType"
                    value="prompts"
                    checked={maliciousDocsInputType === "prompts"}
                    onChange={(e) => setMaliciousDocsInputType(e.target.value)}
                  />
                  <span className="generate-radio-label">{t("redTeamingDocs.maliciousPrompts")}</span>
                </label>
                <label className={`generate-radio-option ${maliciousDocsInputType === "malicious_text" ? "active" : ""}`}>
                  <input
                    type="radio"
                    name="maliciousDocsInputType"
                    value="malicious_text"
                    checked={maliciousDocsInputType === "malicious_text"}
                    onChange={(e) => setMaliciousDocsInputType(e.target.value)}
                  />
                  <span className="generate-radio-label">{t("redTeamingDocs.maliciousTextUrls")}</span>
                </label>
              </div>
            </div>

            <div className="generate-form-group generate-malicious-text-group">
              <label className="generate-form-label">
                {maliciousDocsInputType === "prompts"
                  ? t("redTeamingDocs.maliciousPrompts")
                  : t("redTeamingDocs.maliciousTextUrls")}
              </label>
              <textarea
                value={maliciousDocsPrompt}
                onChange={(e) => setMaliciousDocsPrompt(e.target.value)}
                placeholder={maliciousDocsInputType === "prompts"
                  ? t("redTeamingDocs.promptsPlaceholder")
                  : t("redTeamingDocs.textPlaceholder")}
                className="generate-form-textarea"
              />
            </div>
          </div>

          <div className="generate-form-section generate-form-group-full">
            <div className="generate-section-header">
              <h4 className="generate-section-title">{t("common.generatedFileOptions")}</h4>
              <p className="generate-section-description">
                {t("redTeamingDocs.fileOptionsDesc")}
              </p>
            </div>

            <div className="generate-output-grid">
              <div className="generate-form-group">
                <label className="generate-form-label">{t("common.fileName")}</label>
                <input
                  type="text"
                  value={maliciousDocsFileName}
                  onChange={(e) => setMaliciousDocsFileName(e.target.value)}
                  className="generate-form-input"
                  placeholder={t("common.fileNamePlaceholder")}
                  required
                />
              </div>

              <div className="generate-form-group">
                <label className="generate-form-label">{t("redTeamingDocs.fileType")}</label>
                <select
                  value={maliciousDocsFileType}
                  onChange={(e) => setMaliciousDocsFileType(e.target.value)}
                  className="generate-form-select generate-form-select-full"
                >
                  <option value="1">{t("fileTypes.all")}</option>
                  <option value="2">{t("fileTypes.text")}</option>
                  <option value="3">{t("fileTypes.html")}</option>
                  <option value="4">{t("fileTypes.csv")}</option>
                  <option value="5">{t("fileTypes.powerpoint")}</option>
                  <option value="6">{t("fileTypes.image")}</option>
                  <option value="7">{t("fileTypes.pdf")}</option>
                  <option value="8">{t("fileTypes.qrCode")}</option>
                  <option value="9">{t("fileTypes.word")}</option>
                  <option value="10">{t("fileTypes.excel")}</option>
                  <option value="11">{t("fileTypes.compressed")}</option>
                  <option value="12">{t("fileTypes.json")}</option>
                </select>
              </div>
            </div>
          </div>

          <div className="generate-form-group generate-form-group-full">
            <button
              onClick={handleGenerateMaliciousDocs}
              disabled={maliciousDocsLoading || !maliciousDocsPrompt.trim() || !maliciousDocsFileName.trim()}
              className="generate-form-button"
            >
              {maliciousDocsLoading ? (
                <>
                  <div className="generate-button-spinner"></div>
                  {t("common.generating")}
                </>
              ) : (
                <>{t("redTeamingDocs.generate")}</>
              )}
            </button>
          </div>
        </div>

        {maliciousDocsError && (
          <div className="generate-status-message generate-status-error">
            <div className="generate-status-icon"></div>
            <div className="generate-status-content">
              {t("common.error", { message: maliciousDocsError })}
            </div>
          </div>
        )}
      </div>

      <StatusResultModal
        open={maliciousDocsModal.open}
        type={maliciousDocsModal.type}
        message={maliciousDocsModal.message}
        onClose={() => setMaliciousDocsModal({ open: false, type: "", message: "" })}
      />
    </>
  );
}

export default RedTeamingDocsTab;
